/**
 * Security & Governance · Checklist P0/P1 con estado persistido.
 */
import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import {
  ShieldCheck, CheckCircle, Clock, WarningCircle, Archive, ArrowsClockwise,
} from "@phosphor-icons/react";

const STATUS_OPTIONS = [
  { value: "open",        label: "Abierto",     icon: WarningCircle, cls: "text-amber-300 border-amber-500/40 bg-amber-500/10" },
  { value: "in_progress", label: "En progreso", icon: Clock,         cls: "text-[var(--ag-gold)] border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10" },
  { value: "done",        label: "Hecho",       icon: CheckCircle,   cls: "text-emerald-300 border-emerald-500/40 bg-emerald-500/10" },
  { value: "deferred",    label: "Aplazado",    icon: Archive,       cls: "text-zinc-400 border-zinc-500/40 bg-zinc-500/10" },
];

const PRIORITY_CLS = {
  P0: "text-red-300 border-red-500/40 bg-red-500/10",
  P1: "text-amber-300 border-amber-500/40 bg-amber-500/10",
  P2: "text-[var(--ag-gold)] border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10",
};

export default function SecurityGovernance() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ total: 0, completed: 0, in_progress: 0, completion_pct: 0 });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/founder-os/security/checklist");
      setItems(data.items || []);
      setMeta({ total: data.total, completed: data.completed, in_progress: data.in_progress, completion_pct: data.completion_pct });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (item, newStatus) => {
    try {
      await api.patch(`/founder-os/security/checklist/${item.id}`, { status: newStatus });
      toast.success(`${item.code} → ${newStatus}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="founder-security">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--ag-gold)] font-semibold flex items-center gap-1.5">
            <ShieldCheck size={11} weight="fill" /> Security & Governance
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-2">
            Deuda P0/P1 de la plataforma
          </h1>
          <p className="text-xs text-[var(--ag-text-dim)] mt-1">
            {meta.completed} hechos · {meta.in_progress} en progreso · {meta.total} totales · {meta.completion_pct}% completado
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-1.5 text-xs text-[var(--ag-text-dim)] hover:text-[var(--ag-gold)] px-3 py-2 rounded border border-[var(--ag-border)] hover:border-[var(--ag-gold)]/50"
        >
          <ArrowsClockwise size={13} /> Refrescar
        </button>
      </header>

      {/* Progress bar */}
      <div className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4">
        <div className="text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold mb-2">
          Progreso global
        </div>
        <div className="h-3 rounded-full bg-[var(--ag-midnight)]/40 overflow-hidden">
          <div className="h-full bg-[var(--ag-gold)] transition-all"
               style={{ width: `${meta.completion_pct}%` }}
               data-testid="security-progress" />
        </div>
        <div className="text-[10px] text-[var(--ag-text-muted)] mt-1 tabular-nums">{meta.completion_pct}%</div>
      </div>

      {/* Checklist */}
      {loading ? (
        <div className="text-xs text-[var(--ag-text-dim)]">Cargando…</div>
      ) : (
        <div className="space-y-3" data-testid="security-list">
          {items.map((s) => {
            const stm = STATUS_OPTIONS.find(o => o.value === s.status) || STATUS_OPTIONS[0];
            return (
              <div key={s.id}
                   className="rounded-lg border border-[var(--ag-border)] bg-[var(--ag-surface)] p-4"
                   data-testid={`security-${s.code}`}>
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-[10px] text-[var(--ag-text-muted)] font-bold">{s.code}</span>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${PRIORITY_CLS[s.priority] || ""}`}>
                        {s.priority}
                      </span>
                      <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[var(--ag-border)] text-[var(--ag-text-muted)]">
                        {s.category}
                      </span>
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border inline-flex items-center gap-1 ${stm.cls}`}>
                        <stm.icon size={9} weight="fill" /> {stm.label}
                      </span>
                    </div>
                    <div className="mt-2 font-display text-base font-bold tracking-tight">{s.title}</div>
                    <p className="text-xs text-[var(--ag-text-dim)] mt-1">{s.description}</p>
                    {s.notes && (
                      <p className="text-[11px] text-[var(--ag-gold)] mt-2 italic">↳ {s.notes}</p>
                    )}
                    {s.blocked_by && (
                      <p className="text-[11px] text-red-400 mt-1">⚠ Bloqueado por: {s.blocked_by}</p>
                    )}
                    {s.estimated_effort && (
                      <p className="text-[10px] text-[var(--ag-text-muted)] mt-1">Esfuerzo estimado: {s.estimated_effort}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[var(--ag-border)] flex-wrap">
                  <span className="text-[10px] uppercase tracking-wider text-[var(--ag-text-muted)] font-semibold">Status:</span>
                  {STATUS_OPTIONS.map((o) => (
                    <button
                      key={o.value}
                      type="button"
                      onClick={() => setStatus(s, o.value)}
                      disabled={s.status === o.value}
                      className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border transition-colors ${
                        s.status === o.value
                          ? `${o.cls} opacity-60 cursor-default`
                          : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:border-[var(--ag-gold)]/50 hover:text-[var(--ag-gold)]"
                      }`}
                      data-testid={`security-${s.code}-set-${o.value}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
